import { create } from "zustand"
import { persist } from "zustand/middleware"

// Types
export interface IncomingCall {
  type?: "user" | "contact"
  name: string
  number: string
  address?: string
  call_sid: string
  matter?: string
  contact?: string
  // Set only when longest_idle routing offered this call — renders queue-style UI
  call_queue?: string
  queue_entry_title?: string
  queue_entry_color?: string
  division_name?: string
}

export interface CurrentCall {
  call_id?: string
  name: string
  number: string
  address?: string
  call_sid: string
  matter?: string
  contact?: string
}

export interface TransferCall {
  _id?: string
  display_name?: string
  type?: string
  can_merge?: boolean
}

export interface QueueEntry {
  _id: string
  contact: {
    _id: string
    display_name?: string
    phone: string
    city?: string
    state?: string
  }
  division: {
    _id: string
    name: string
  }
  matter?: string
  type: "hold" | "callback"
  queue_entry_title?: string
  queue_entry_color?: string
  call?: string
}

export type BrowserDialingState = "not_setup" | "loading" | "ready" | "error"
export type AgentStatus = "available" | "away" | "dnd" | "on_call" | "ringing"

interface CallState {
  // Twilio state
  browserDialing: BrowserDialingState
  incomingCall: IncomingCall | null
  currentCall: CurrentCall | null
  transferCall: TransferCall | null

  // Call controls
  muted: boolean
  onHold: boolean

  // Queue
  queueEntry: QueueEntry | null

  // UI state
  showKeypad: boolean
  showTransferModal: boolean
  callStartTime: number | null

  // Agent status
  agentStatus: AgentStatus
  isInQueue: boolean

  // Actions
  setBrowserDialing: (state: BrowserDialingState) => void
  setIncomingCall: (call: IncomingCall | null) => void
  setCurrentCall: (call: CurrentCall | null) => void
  hydrateCurrentCall: (call: CurrentCall) => void
  updateCallSid: (callSid: string) => void
  setTransferCall: (call: TransferCall | null) => void
  setMuted: (muted: boolean) => void
  setOnHold: (onHold: boolean) => void
  setQueueEntry: (entry: QueueEntry | null) => void
  setShowKeypad: (show: boolean) => void
  setShowTransferModal: (show: boolean) => void
  setCallStartTime: (time: number | null) => void
  setAgentStatus: (status: AgentStatus) => void
  setIsInQueue: (inQueue: boolean) => void

  // Computed
  getAgentStatus: () => AgentStatus

  // Reset
  resetCallState: () => void
}

const CALL_STORE_VERSION = 2

export const useCallStore = create<CallState>()(
  persist(
    (set, get) => ({
  // Initial state
  browserDialing: "not_setup",
  incomingCall: null,
  currentCall: null,
  transferCall: null,
  muted: false,
  onHold: false,
  queueEntry: null,
  showKeypad: false,
  showTransferModal: false,
  callStartTime: null,
  agentStatus: "available",
  isInQueue: false,

  // Actions
  setBrowserDialing: (state) => set({ browserDialing: state }),

  setIncomingCall: (call) =>
    set({
      incomingCall: call,
      agentStatus: call ? "ringing" : get().currentCall ? "on_call" : "available",
    }),

  setCurrentCall: (call) =>
    set({
      currentCall: call,
      callStartTime: call ? Date.now() : null,
      agentStatus: call ? "on_call" : "available",
      muted: false,
      onHold: false,
    }),

  hydrateCurrentCall: (call) =>
    set((state) => {
      const isSameCall = !!state.currentCall && (
        (!!call.call_id && state.currentCall.call_id === call.call_id) ||
        (!!call.call_sid && state.currentCall.call_sid === call.call_sid)
      )

      const mergedCall = state.currentCall
        ? {
            ...state.currentCall,
            ...call,
            call_sid: call.call_sid || state.currentCall.call_sid || "",
          }
        : call

      return {
        currentCall: mergedCall,
        callStartTime: isSameCall ? state.callStartTime ?? Date.now() : Date.now(),
        agentStatus: "on_call",
        muted: isSameCall ? state.muted : false,
        onHold: isSameCall ? state.onHold : false,
        transferCall: isSameCall ? state.transferCall : null,
        showKeypad: isSameCall ? state.showKeypad : false,
        showTransferModal: isSameCall ? state.showTransferModal : false,
      }
    }),

  updateCallSid: (callSid) =>
    set((state) => ({
      currentCall: state.currentCall
        ? { ...state.currentCall, call_sid: callSid }
        : null,
    })),

  setTransferCall: (call) => set({ transferCall: call }),

  setMuted: (muted) => set({ muted }),

  setOnHold: (onHold) => set({ onHold }),

  setQueueEntry: (entry) => set({ queueEntry: entry }),

  setShowKeypad: (show) => set({ showKeypad: show }),

  setShowTransferModal: (show) => set({ showTransferModal: show }),

  setCallStartTime: (time) => set({ callStartTime: time }),

  setAgentStatus: (status) => set({ agentStatus: status }),

  setIsInQueue: (inQueue) => set({ isInQueue: inQueue }),

  // Computed status based on call state
  getAgentStatus: () => {
    const state = get()
    if (state.currentCall) return "on_call"
    if (state.incomingCall) return "ringing"
    if (state.browserDialing !== "ready") return "available"
    return state.agentStatus
  },

  // Reset all call-related state
  resetCallState: () =>
    set({
      incomingCall: null,
      currentCall: null,
      transferCall: null,
      muted: false,
      onHold: false,
      showKeypad: false,
      showTransferModal: false,
      callStartTime: null,
      agentStatus: "available",
    }),
    }),
    {
      name: "archk-call-state",
      version: CALL_STORE_VERSION,
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name)
          return str ? JSON.parse(str) : null
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value))
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name)
        },
      },
      migrate: (persistedState, version) => {
        const state = persistedState as Partial<CallState> | undefined

        if (version < CALL_STORE_VERSION && (state?.currentCall || state?.callStartTime)) {
          console.warn("[call-store] Dropping legacy persisted active-call state during migration")
        }

        return {
          isInQueue: state?.isInQueue ?? false,
        } as CallState
      },
      partialize: (state) => ({
        isInQueue: state.isInQueue,
      }) as CallState,
    },
  ),
)
